"use client";
import { useState, useEffect, useRef } from "react";
import { cookieName, cookieIsChangeLang } from "@/app/i18n/settings";
import { setCookie } from "cookies-next";
import { usePathname, useSearchParams, useRouter } from "next/navigation";
import { useTranslation } from "@/app/i18n/client";
import { ChevronDown, Check, Search, MenuIcon } from "lucide-react";
import Link from "next/link";
import { Input } from "./ui/input";
import { cn } from "@/lib/utils";
import { ShoppingCatagory } from "./nav-ulife-server-component";
import { CUSTOMER_LEVEL } from "@prisma/client";
import { WebNavigationComponent } from "@/util/query/navigation-query";
import useIsMobile from "@/components/useIsMobile";
import { Language } from "@/app/i18n/settings";
import { HeaderItemType } from "@/util/query/header-query";
import ColorStyleContext from "@/lib/providers/color-style-privider";
import { useContext } from "react";

// DYANMIC IMPORT
import dynamic from "next/dynamic";
const NavLogInClient = dynamic(() => import("./nav-login-client").then((mod) => mod.NavLogInClient), {
    ssr: false,
    loading: () => <MenuIcon className="mx-[10px] w-[22px] h-[22px] justify-center" />,
});
const ButtonNotification = dynamic(() => import("./notification/button-notification"), { ssr: false });
const ButtonCart = dynamic(() => import("./button-cart"), { ssr: false });
const ShoppingNav = dynamic(() => import("@/components/business/navigation/templates/shopping.nav"), {
    ssr: false,
});
const BusinessNav = dynamic(() => import("@/components/business/navigation/templates/business.nav"), {
    ssr: false,
});

type LangOptions = {
    value: Language;
    label: string;
}[];

type NavMobileMenuLazyLoadProps = {
    shopcats: ShoppingCatagory[];
    login: boolean;
    level: CUSTOMER_LEVEL;
    headerItem: HeaderItemType[] | null;
    navShow?: boolean;
};

type Props = {
    webNavigation: WebNavigationComponent | null;
    template: string | null;
};

export const LanguageSelectionNav = ({ clasName, options }: { clasName?: string; options: any[] }) => {
    const pathName = usePathname();
    const router = useRouter();
    const { lang } = useTranslation();
    const [selectedLang, setSelectedLang] = useState(lang);
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const langOptions = options;

    useEffect(() => {
        setSelectedLang(lang);
    }, [lang]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const handleOnValueChanged = (value: Language) => {
        setSelectedLang(value);
        setCookie(cookieName, value);
        setCookie(cookieIsChangeLang, "true");
        router.push(pathName);
        window.location.reload();
        setIsOpen(false);
    };

    const toggleDropdown = () => setIsOpen(!isOpen);

    return (
        <div className={cn("relative inline-block w-32", clasName)} ref={dropdownRef}>
            <button
                onClick={toggleDropdown}
                className="pl-3 py-2 bg-transparent border border-white rounded-full cursor-pointer focus:outline-none w-full text-left text-sm">
                {langOptions.map((option) => {
                    if (option.value === selectedLang) {
                        return option.label;
                    }
                })}
                <span className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <ChevronDown className="h-4 w-4 opacity-90" />
                </span>
            </button>

            {isOpen && (
                <div className="absolute left-0 mt-1 w-full bg-white border border-gray-300 rounded shadow-md z-10 p-1">
                    {langOptions.map((option, index) => (
                        <div className="cursor-pointer" key={index}>
                            <div
                                key={option.value}
                                onClick={() => handleOnValueChanged(option.value)}
                                className={`px-2 h-8 flex items-center hover:bg-gray-200 rounded text-sm text-black ${
                                    selectedLang === option.value ? "bg-gray-100" : ""
                                }`}>
                                <span className="w-6 flex items-center justify-start">
                                    {selectedLang === option.value && <Check className="h-4 w-4" />}
                                </span>
                                {option.label}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export const NavSearch = ({ clasName, onEnter, colorStyle }: { clasName?: string; onEnter?: () => void; colorStyle: any }) => {
    const searchparam = useSearchParams();
    const [searchInput, setSearchInput] = useState(searchparam.get("search") ?? "");
    const { t } = useTranslation();
    const rout = useRouter();

    const pathname = usePathname();
    const handleSearchInputChange = (e: any) => {
        setSearchInput(e.target.value);
    };

    const handleSearchEnter = (e: any) => {
        const kb = e as KeyboardEvent;
        if (kb.key.toLowerCase() == "enter") {
            e.preventDefault();
            if (searchInput != "") {
                rout.push("/search?search=" + searchInput);
                if (onEnter) {
                    onEnter();
                }
            }
        }
    };

    useEffect(() => {
        setSearchInput(searchparam.get("search") ?? "");
    }, [pathname, searchparam]);

    return (
        <div 
            className={cn("flex items-center max-w-[280px] border rounded-full px-4 gap-1 ", clasName)}
        >
            <Link 
                href={searchInput != "" ? "/search?search=" + searchInput : ""} aria-label="search"
                style={{ backgroundColor: colorStyle?.headerBg || "#ffffff", color: colorStyle?.text || "#3d3d3d" }}
            >
                <Search 
                    className="w-4 h-4" 
                    style={{ backgroundColor: colorStyle?.headerBg || "#ffffff", color: colorStyle?.text || "#3d3d3d" }}
                ></Search>
            </Link>

            <Input
                className="w-full text-sm font-light border-none h-full focus-visible:ring-0 focus-visible:ring-offset-0 dynamic-color dynamic-bg"
                type="text"
                id="searchInput"
                value={searchInput}
                onChange={handleSearchInputChange}
                onKeyDown={handleSearchEnter}
                placeholder={t("navigation.search")}
                style={
                    {
                        "--dynamic-color": colorStyle?.text || "#3d3d3d",
                        "--dynamic-bg": colorStyle?.headerBg || "#ffffff",
                    } as React.CSSProperties
                }
            />
        </div>
    );
};

export const ButtonNotificationLazyLoad = () => {
    const [show, setShow] = useState(false);

    useEffect(() => {
        // Load after the page has hydrated
        setShow(true);
    }, []);

    return <>{show && <ButtonNotification />}</>;
};

export const ButtonCartLazyLoad = () => {
    const [show, setShow] = useState(false);

    const colorStyle = useContext(ColorStyleContext);

    useEffect(() => {
        // Load after the page has hydrated
        setShow(true);
    }, []);

    return <>{show && <ButtonCart colorStyle={colorStyle? colorStyle.text : "#3d3d3d"}/>}</>;
};

const useIsScroll = () => {
    const [isMove, setIsMove] = useState<boolean>(false);

    useEffect(() => {
        const handleMove = () => {
            setIsMove(true);
        };

        document.addEventListener("scroll", handleMove, { passive: true });

        return () => {
            document.removeEventListener("scroll", handleMove);
        };
    }, []);

    return isMove;
};

export const NavMobileMenuLazyLoad = ({ shopcats, login, level, headerItem }: NavMobileMenuLazyLoadProps) => {
    const [show, setShow] = useState(false);
    const [trigger, setTrigger] = useState(false);
    const isMobile = useIsMobile();
    const isScroll = useIsScroll();
    const isClick = useRef(false);

    const handleClick = () => {
        isClick.current = true;
        setShow(true);
        setTrigger(!trigger);
    };

    useEffect(() => {
        if (isMobile && (isScroll || isClick.current)) {
            setShow(true);
        }
    }, [isMobile, isScroll]);

    return (
        <div>
            {show ? (
                <NavLogInClient 
                    shopcats={shopcats} 
                    login={login} 
                    level={level} 
                    trigger={trigger} 
                    headerItem={headerItem}
                />
            ) : (
                <MenuIcon className="mx-[10px] w-[22px] h-[22px] justify-center" onClick={handleClick} />
            )}
        </div>
    );
};

export const BusinessNavLazyLoad = ({ webNavigation, template }: Props) => {
    const [show, setShow] = useState(false);
    const isMobile = useIsMobile();

    useEffect(() => {
        if (isMobile === false) {
            setShow(true);
        }
    }, [isMobile]);

    return (
        <div>
            {show && (
                <div className="w-full flex gap-4 items-center">
                    {template === "TEMPLATE_1" ? (
                        <ShoppingNav webNavigation={webNavigation} />
                    ) : template === "TEMPLATE_2" ? (
                        <BusinessNav webNavigation={webNavigation} />
                    ) : (
                        <BusinessNav webNavigation={webNavigation} />
                    )}
                </div>
            )}
        </div>
    );
};
